// based on angular2-odata
// https://github.com/gallayl/angular2-odata
import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable } from 'rxjs'
import {
  DeleteOperation,
  GetOperation,
  ODataConfigurationBase,
  ODataFunction,
  ODataQuery,
  PatchOperation,
  PostOperation,
  PutOperation,
  TransformationService
} from './query'
import { map } from 'rxjs/operators'

@Injectable()
export class ODataService<T> {
  public typeName: string

  constructor(
    protected http: HttpClient,
    protected config: ODataConfigurationBase,
    protected transformations: TransformationService<T> = null
  ) {}

  public get(key: number | string): GetOperation<T> {
    return new GetOperation<T>(this.typeName, this.config, this.http, key, this.transformations)
  }

  public post(entity: T): PostOperation<T> {
    return new PostOperation<T>(this.typeName, this.config, this.http, entity, this.transformations)
  }

  public patch(entity: any, key: number | string): PatchOperation<T> {
    return new PatchOperation<T>(
      this.typeName,
      this.config,
      this.http,
      key,
      entity,
      this.transformations
    )
  }

  public put(entity: T, key: number | string): PutOperation<T> {
    return new PutOperation<T>(
      this.typeName,
      this.config,
      this.http,
      key,
      entity,
      this.transformations
    )
  }

  public delete(key: number | string): DeleteOperation<T> {
    return new DeleteOperation<T>(this.typeName, this.config, this.http, key, this.transformations)
  }

  public query(
    functionInfo: ODataFunction = null,
    customParams: { name: string; value: string }[] = []
  ): ODataQuery<T> {
    return new ODataQuery<T>(
      this.typeName,
      this.config,
      this.http,
      functionInfo,
      this.transformations,
      customParams
    )
  }

  public customAction(
    key: number | string,
    actionName: string,
    postdata: any = {}
  ): Observable<any> {
    return this.http.post(this.getEntityUri(key) + '/' + actionName, postdata, {
      headers: this.config.postRequestHeaders
    })
  }

  public customFunction(
    key: number | string,
    actionName: string,
    disableCache = false
  ): Observable<any> {
    const options = disableCache ? this.config.requestOptionsNoCache : this.config.requestOptions
    return this.http.get(this.getEntityUri(key) + '/' + actionName, options)
  }

  public customFunctionFile(
    key: number | string,
    actionName: string,
    accept: string = null,
    contentType: string = null
  ): Observable<any> {
    return this.http.get(this.getEntityUri(key) + '/' + actionName, {
      headers: this.config.getCustomHeaders(accept, contentType)
    })
  }

  public restGet(actionName: string = null): Observable<any> {
    return this.http
      .get(
        this.getEntityUri('') + (!!actionName ? '/' + actionName : ''),
        this.config.requestOptions
      )
      .pipe(
        map(response => {
          if (!this.transformations) {
            return response
          }

          return this.transformations.responseTransformArray(response, 'GET')
        })
      )
  }

  public restPost(postdata: any, actionName: string = null): Observable<any> {
    return this.http
      .post(this.getEntityUri('') + (!!actionName ? '/' + actionName : ''), postdata, {
        headers: this.config.postRequestHeaders
      })
      .pipe(
        map(response => {
          if (!this.transformations) {
            return response
          }

          return this.transformations.responseTransformArray(response, 'POST')
        })
      )
  }

  protected getEntityUri(key: number | string): string {
    return this.config.getEntityUri(key, this.typeName)
  }
}
